package er.prateek.crossly.progression

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.Puzzle
import er.prateek.crossly.data.model.UserProgress
import er.prateek.crossly.firebase.CompiledDefaultsRemoteConfigClient
import er.prateek.crossly.firebase.RemoteConfigClient
import er.prateek.crossly.firebase.RemoteConfigKeys
import er.prateek.crossly.persistence.ProgressStore

/**
 * Compose-observable progress: best stars per puzzle, plus the level/tier
 * unlock rules derived from it. Backed by [ProgressStore] — loaded once at
 * construction, saved on every change — so progress survives a relaunch.
 */
class ProgressionManager(
    private val store: ProgressStore,
    private val remoteConfig: RemoteConfigClient = CompiledDefaultsRemoteConfigClient,
) {
    private val bestStarsByPuzzleId = mutableStateMapOf<String, Int>().apply {
        putAll(store.load().starsByPuzzleId)
    }
    private val bestTimeMsByPuzzleId = mutableStateMapOf<String, Long>().apply {
        putAll(store.load().bestTimeMsByPuzzleId)
    }
    private var starsSpent: Int by mutableStateOf(store.load().starsSpent)

    fun starsFor(puzzleId: String): Int = bestStarsByPuzzleId[puzzleId] ?: 0

    /** Every star ever earned across every puzzle -- the spendable balance's upper bound. */
    fun totalStarsEarned(): Int = bestStarsByPuzzleId.values.sum()

    /** Distinct puzzles with at least one recorded completion -- used to gate the notification permission ask. */
    fun completedPuzzleCount(): Int = bestStarsByPuzzleId.size

    /** Puzzles solved for the full three stars -- feeds the "perfect solve" badges. */
    fun perfectPuzzleCount(): Int = bestStarsByPuzzleId.values.count { it >= 3 }

    /** Stars available to spend right now: earned minus spent, never negative. */
    fun starsAvailable(): Int = (totalStarsEarned() - starsSpent).coerceAtLeast(0)

    /**
     * Spends one star if the balance allows it. Returns whether the spend went through, so a
     * caller (letter reveal) can tell "granted" from "no stars left" without a separate check
     * that could race a concurrent spend.
     */
    fun spendStar(amount: Int = 1): Boolean {
        if (amount <= 0) return true
        if (starsAvailable() < amount) return false
        starsSpent += amount
        store.save(store.load().copy(starsSpent = starsSpent))
        return true
    }

    fun bestTimeMs(puzzleId: String): Long? = bestTimeMsByPuzzleId[puzzleId]

    /** Any earned stars or recorded solve time — used to hide "start from fresh" on inbound links. */
    fun hasAnyProgress(): Boolean =
        bestStarsByPuzzleId.isNotEmpty() || bestTimeMsByPuzzleId.isNotEmpty()

    fun recordCompletion(puzzleId: String, hintsUsed: Int, mistakes: Int, elapsedMs: Long = 0): Int {
        val earned = ScoringPolicy.starsFor(hintsUsed, mistakes)
        val current = bestStarsByPuzzleId[puzzleId] ?: 0
        var changed = false
        if (earned > current) {
            bestStarsByPuzzleId[puzzleId] = earned
            changed = true
        }
        if (elapsedMs > 0) {
            val best = bestTimeMsByPuzzleId[puzzleId]
            if (best == null || elapsedMs < best) {
                bestTimeMsByPuzzleId[puzzleId] = elapsedMs
                changed = true
            }
        }
        if (changed) {
            // Read-modify-write: this shares one UserProgress record/settings key
            // with DailyPuzzleManager's dailyStreak, so a blind overwrite here
            // would silently wipe out the streak.
            store.save(
                store.load().copy(
                    starsByPuzzleId = bestStarsByPuzzleId.toMap(),
                    bestTimeMsByPuzzleId = bestTimeMsByPuzzleId.toMap(),
                ),
            )
        }
        return earned
    }

    fun resetProgress() {
        bestStarsByPuzzleId.clear()
        bestTimeMsByPuzzleId.clear()
        starsSpent = 0
        // Only clears level/star progress -- the daily streak is a separate,
        // independent track (per plan) and isn't affected by this reset.
        store.save(
            store.load().copy(
                starsByPuzzleId = emptyMap(),
                starsSpent = 0,
                bestTimeMsByPuzzleId = emptyMap(),
            ),
        )
    }

    fun isTierUnlocked(tier: DifficultyTier, puzzlesByTier: Map<DifficultyTier, List<Puzzle>>): Boolean {
        if (tier == DifficultyTier.EASY) return true
        val previousTier = DifficultyTier.entries[tier.ordinal - 1]
        val previousPuzzles = puzzlesByTier[previousTier].orEmpty()
        if (previousPuzzles.isEmpty()) return false
        val starredCount = previousPuzzles.count { starsFor(it.id) >= 1 }
        return starredCount.toDouble() / previousPuzzles.size >= tierUnlockThresholdPct()
    }

    fun isLevelUnlocked(tierUnlocked: Boolean, puzzlesInTier: List<Puzzle>, index: Int): Boolean {
        if (!tierUnlocked) return false
        if (index == 0) return true
        return starsFor(puzzlesInTier[index - 1].id) >= 1
    }

    /**
     * Remote Config-tunable (`tier_unlock_threshold_pct`, docs/PLAN.md), falling back to the
     * compiled default whenever the value hasn't loaded yet (0, before the first successful
     * `warmUp()`) or is out of range -- never lets a mid-flight fetch unlock everything at 0%.
     */
    private fun tierUnlockThresholdPct(): Double {
        val pct = remoteConfig.getLong(RemoteConfigKeys.TIER_UNLOCK_THRESHOLD_PCT)
        return if (pct in 1..100) pct / 100.0 else TIER_UNLOCK_THRESHOLD_PCT
    }

    companion object {
        const val TIER_UNLOCK_THRESHOLD_PCT = 0.75
    }
}
